/**
 * Converts Cadence 1.0 (Journey) steps into Cadence 2.0 flow editor steps.
 *
 * Journey steps use `day_number` to express timing (absolute day in sequence).
 * We diff consecutive day_numbers to derive wait durations.
 *
 * Journey 1.0 emails are always manual — they map to task steps, not automated email steps.
 */

const JOURNEY_ACTION_TO_TASK_TYPE = {
  EMAIL: 'email',
  CALL: 'call',
  SMS: 'sms',
  TEXT: 'sms',
  IN_PERSON: 'other',
  VISIT: 'other',
  LINKEDIN: 'linkedin',
  PRINT_MAIL: 'print_mail',
  THANKVIEW: 'thankview',
  THANK_VIEW: 'thankview',
  RESEARCH: 'research',
};

const generateStepId = () => `step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Translates an array of Journey 1.0 step objects (from journeyTasksByJourneyId)
 * into the Cadence 2.0 steps array consumed by CadenceFlowEditor.
 *
 * @param {Array} journeySteps - raw steps from fetchStepsByJourneyId
 * @returns {Array} cadence 2.0 steps (task + wait interleaved)
 */
export const convertJourneyStepsToV2Steps = (journeySteps = []) => {
  const active = journeySteps
    .filter((s) => s.active !== false)
    .sort((a, b) => (a.step_number || 0) - (b.step_number || 0) || (a.day_number ?? 0) - (b.day_number ?? 0));

  const result = [];

  active.forEach((step, i) => {
    const currentDay = step.day_number ?? 0;
    const prevDay = i === 0 ? currentDay : (active[i - 1].day_number ?? 0);
    const dayDiff = currentDay - prevDay;

    if (i > 0 && dayDiff > 0) {
      result.push({
        id: generateStepId(),
        type: 'wait',
        waitDuration: dayDiff,
        waitUnit: 'days',
      });
    }

    const actionType = (step.default_action_type || 'OTHER').toUpperCase();
    const taskType = JOURNEY_ACTION_TO_TASK_TYPE[actionType] || 'other';

    result.push({
      id: generateStepId(),
      type: 'task',
      taskType,
      name: step.title || '',
      description: step.description || '',
    });
  });

  return result;
};
